import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { Accept } from 'react-dropzone';
import { TRcsTypes } from './types/dataProvider';

dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * Utility Constants
 */

/**
 * Array of Related content types for related content recognition
 */
export const rcsTypes: TRcsTypes[] = [
  'bio',
  'correction',
  'fact',
  'highlights',
  'info',
  'question',
  'quote',
  'refer',
  'update',
];

/**
 * Email Regex
 * https://emailregex.com/index.html
 * https://html.spec.whatwg.org/multipage/input.html#input.email.attrs.value.multiple
 * */
export const emailRegex =
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Unicode characters
export const emdash = '\u2014';
export const endash = '\u2013';
export const dash = '\u2010';
export const ellipsis = '\u2026';
export const verticalEllipsis = '\u22EE';
export const nbsp = '\u00A0';
export const hairSpace = '\u200A'; // &hairsp; / &VeryThinSpace;
export const moneyWingsEmoji = '\u{1F4B8}'; // or \uD83D\uDCB8
export const lightbulbEmoji = '\u{1F4A1}'; // or \uD83D\uDCA1
export const bullet = '\u2022';
export const largeDot = '\u25cf';
export const wavingHandEmoji = '\u{1F44B}';

/**
 * DayJs format() strings
 * Reference: https://day.js.org/docs/en/display/format
 */

// For datetime-local input fields like RA's DateTimeInput format
export const DAYJS_DATETIME_LOCAL_FORMAT = 'YYYY-MM-DD[T]HH:mm';

// ISO-8601 Example 2020-04-02T08:02:17-05:00
export const DAYJS_ISO_8601 = 'YYYY-MM-DDTHH:mm:ssZ[Z]';

// For use in formatDate() format prop
export const ISO_8601 = 'ISO8601';

/**
 * INTL Date Formats
 * Reference for short codes:
 * https://day.js.org/docs/en/display/format
 */

// 08:59 PM
export const HHMM: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};

// 2024
export const YYYY: Intl.DateTimeFormatOptions = {
  year: 'numeric',
};

// April 2022
export const MMMM_YYYY: Intl.DateTimeFormatOptions = {
  month: 'long',
  year: 'numeric',
};

// 12/31/21
export const MM_DD_YY: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
};

// 2/3/25, 2:02 PM (as UTC for certain input fields)
export const UTC_MM_DD_YY_HHMM: Intl.DateTimeFormatOptions = {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'UTC',
};

// 12/31/21, 8:59 PM
export const MM_DD_YY_HHMM: Intl.DateTimeFormatOptions = {
  dateStyle: 'short',
  timeStyle: 'short',
};

// 01/23/2026, 3:21 PM
export const MM_DD_YYYY_C_12HHMM: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

// Sep 23, 2024
export const MMM_DD_YYYY: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
};

// Sep 23, 2024 at 8:59 PM
export const MMM_DD_YYYY_HHMM: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

// Sep 23, 2024, 8:59 PM
export const MMM_DD_YYYY_C_12HHMM: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
};

// Wed, 6/6/25, 12:50 AM
export const WEEKDAY_MMM_DD_YY_HHMM: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'numeric',
  day: 'numeric',
  year: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

// Friday, December 31, 2021
export const WEEKDAY_MMMM_DD_YYYY: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  month: 'long',
  day: '2-digit',
  year: 'numeric',
};

// Friday, December 31, 2021 at 8:59 PM
export const WEEKDAY_MMMM_DD_YYYY_HHMM: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  month: 'long',
  day: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

// Date/Time Constants generally for mui datepickers
export const absMaxDateTime = dayjs('9999-12-31T23:59:59.999Z').utc(); // note: can possibly lag datepickers open
export const absMinDateTime = dayjs('100-01-01T00:00:00.000Z').utc();
export const safeMaxDateTime = dayjs('2500-12-31T23:59:59.999Z').utc(); // more reasonable max / mins
export const safeMinDateTime = dayjs('1500-01-01T00:00:00.000Z').utc();

// App Constants
export const appsWithAssets = [
  'business',
  'calendar',
  'classifieds',
  'editorial',
  'eedition',
  'electionsstats',
];

// 32-bit min-max values for various applications 2,147,483,647 / -2,147,483,648
export const int32Max = 2147483647;
export const int32Min = -2147483648;

// lowercase - check if your input props name / autocomplete value matches any of the following
// then you should mark it as data-private for logrocket masking purposes.
// reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
export const PRIVATE_FIELDS = [
  'shipping',
  'billing',
  'home',
  'work',
  'mobile',
  'fax',
  'page',
  'tel',
  'tel-country-code',
  'tel-national',
  'tel-area-code',
  'tel-local',
  'tel-extension',
  'email',
  'impp',
  'name',
  'honorific-prefix',
  'given-name',
  'additional-name',
  'family-name',
  'honorific-suffix',
  'nickname',
  'username',
  'new-password',
  'current-password',
  'one-time-code',
  'organization-title',
  'organization',
  'street-address',
  'address',
  'address-line1',
  'address-line2',
  'address-line3',
  'address-level1',
  'address-level2',
  'address-level3',
  'address-level4',
  'country',
  'country-name',
  'postal-code',
  'cc-name',
  'cc-given-name',
  'cc-additional-name',
  'cc-family-name',
  'cc-number',
  'cc-exp',
  'cc-exp-month',
  'cc-exp-year',
  'cc-csc',
  'cc-type',
  'bday',
  'bday-day',
  'bday-month',
  'bday-year',
  'sex',
  'photo',
  'webauthn',
] as const;

/**
 * File Upload Input MIME Types for React-Admin Inputs and InlineAsset
 *
 * NOTE: There are two formats here, as React-Admin uses the
 * string array format, and react-dropzone use the object format.
 *
 * The dropzone used to operate on the string array format, but
 * the library has since changed to the object format, the object
 * key is mime type, and the value is an array of extensions.
 *
 * Components like <FileFieldInput> will require the object format.
 **/

/**
 * NOTE: Some unaccepted zip types (bz, bz2, 7z, rar) will
 * misclassify themselves as an accepted type, so in this case the client cant reject instantly
 * but the backend will respond with an invalid type upon upload completion
 */
export const acceptFileMimeArray: string[] = [
  // --- Archive file types ---
  'application/zip', // IANA RFC official .zip
  'application/x-zip-compressed', // windows .zip type
  'application/zip-compressed', // non-standard .zip used by some programs
  'application/pdf', // .pdf
  'application/msword', // .doc, .dot, .wiz
  'application/dot', // .dot word template
  'application/vnd.ms-powerpoint', // .ppt, .pot, .pps, .ppa
  'application/vnd.ms-excel', // .xla, .xlb, .xlc, .xlm, .xls, .xlt, .xlw
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
];

/**
 * This is the object format for react-dropzone accept prop
 * See: https://react-dropzone.js.org/#section-accepting-specific-file-types
 */
export const acceptFileMimeObject: Accept = {
  'application/zip': [],
  'application/x-zip-compressed': [],
  'application/zip-compressed': [],
  'application/pdf': [],
  'application/msword': [],
  'application/dot': [],
  'application/vnd.ms-powerpoint': [],
  'application/vnd.ms-excel': [],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': [],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
};

// Accepted Image Upload Input MIME for react-dropzone
export const acceptImageMimeObject: Accept = {
  'image/jpeg': [],
  'image/png': [],
  'image/gif': [],
  'image/tiff': [],
  'image/webp': [],
};

// Regex: image file extensions allowed in URLs
export const urlAllowedImageFileExtensionRegExp = /\.(jpeg|jpg|png|gif|tif|tiff|webp)$/i;

// Audio assets - allowing mpeg (mp3, etc)
export const acceptAudioMimeArray: string[] = ['audio/mpeg'];

export const acceptAudioMimeObject: Accept = {
  'audio/mpeg': [],
};

export const urlAllowedAudioFileExtensionRegExp = /\.(mp3)$/i;

export const acceptVideoMimeArray: string[] = [
  'video/*', // Accepts all video types
  'video/x-ms-wmv', // Windows Media Video
  'application/x-mpegURL', // HTTP Live Streaming
];

export const acceptVideoMimeObject: Accept = {
  'video/*': ['video/x-ms-wmv', 'application/x-mpegURL'], // 'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/x-ms-wmv'
};

export const urlAllowedVideoFileExtensionRegExp = /\.(mp4|webm|ogg|mov|avi|wmv|hls)$/i;

// Accepted Caption Upload Input MIME Types Array for Caption Asset and previews
// '.ttml' is not supported by the backend currently.
export const acceptCaptionMimeArray: string[] = ['.vtt', '.srt'];
export const acceptCaptionMimeObject: Accept = {
  'text/vtt': [],
  'text/srt': [],
  'application/x-subrip': [],
};

// Caption file extensions allowed in URLs
export const urlAllowedCaptionFileExtensionRegExp = /\.(vtt|srt)$/i;

/** KPI bar */
export const kpiBarExpandedMinHeight = '116px';

// TEMP SyncedStore to Form sync delay
// Planning to move away from this in the future to a different form context to avoid react admin form snap back
// A short delay to allow Ydoc and SyncedStore to settle before writing to Form
export const SYNC_DELAY_MS = 500;

// CMSADMIN-2025 Collection file types - schema for react-dropzone.
// See: https://react-dropzone.js.org/#section-accepting-specific-file-types
export const acceptedCollectionFileTypes: Accept = {
  // File types (documents, archives, etc.)
  ...acceptFileMimeObject,
  // Audio types
  ...acceptAudioMimeObject,
  // Image types
  ...acceptImageMimeObject,
  // Video types
  ...acceptVideoMimeObject,
};
